import type {EstablishedAhpSessionIdentity, PreparedPrompt} from '@github-ui/ahp-session-client/contracts'
import {preparedPromptPayloadChars} from '@github-ui/ahp-session-client/contracts'
import {createHandoffStorage, type HandoffFailure} from '@github-ui/ahp-session-client/handoff-storage'
import {SUBMIT_ERROR_MESSAGE} from '@github-ui/ahp-session-client/submission-error'
import {getCurrentUserLogin} from '@github-ui/client-env'

const TASK_STARTUP_INPUT_VERSION = 1
const STORAGE_KEY_PREFIX = 'github.task-startup-input.'
const TASK_STARTUP_INPUT_METADATA_RESERVE_CHARS = 4096
export const TASK_STARTUP_HOST_REJECTION_MESSAGE = 'This message was not sent because the host rejected it.'
export const TASK_STARTUP_UNCONFIRMED_DELIVERY_MESSAGE = 'Delivery could not be confirmed. The message remains queued.'
export const TASK_STARTUP_DISCONNECTED_MESSAGE = 'Delivery could not be confirmed because the session disconnected.'

export type TaskStartupQueuedPromptStatus = 'admitted' | 'dispatching' | 'host-queued' | 'rejected'
export type TaskStartupInputOrigin = 'startup' | 'existing-conversation'

export interface TaskStartupQueuedPrompt {
  readonly id: string
  readonly prompt: PreparedPrompt
  readonly status: TaskStartupQueuedPromptStatus
  readonly failure?: string
}

export interface TaskStartupInputRecord {
  readonly version: typeof TASK_STARTUP_INPUT_VERSION
  readonly origin?: TaskStartupInputOrigin
  readonly taskId: string
  readonly launchId: string
  readonly owner: string
  readonly revision: number
  readonly identity?: EstablishedAhpSessionIdentity
  readonly firstPromptAccepted: boolean
  readonly handoffReady: boolean
  readonly queuedPrompts: readonly TaskStartupQueuedPrompt[]
}

export type TaskStartupPromptAdmission =
  | {readonly status: 'accepted'; readonly id: string}
  | {readonly status: 'refused'; readonly reason: HandoffFailure | 'superseded'; readonly message: string}

const listeners = new Set<() => void>()
const records = new Map<string, TaskStartupInputRecord | null>()
const initializationFailures = new Map<string, {launchId: string; owner: string; reason: HandoffFailure}>()
const restoredDispatches = new Set<string>()

function queuedPromptKey(taskId: string, launchId: string, id: string): string {
  return `${taskId}:${launchId}:${id}`
}

function isExistingConversationInput(record: TaskStartupInputRecord): boolean {
  return record.origin === 'existing-conversation'
}

function notify(): void {
  for (const listener of listeners) listener()
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null
}

function isPreparedPrompt(value: unknown): value is PreparedPrompt {
  if (!isRecord(value) || typeof value.text !== 'string') return false
  if (value.attachments !== undefined && !Array.isArray(value.attachments)) return false
  if (value.model !== undefined && !isRecord(value.model)) return false
  if (value.agent !== undefined && !isRecord(value.agent)) return false
  if (value.contributedInstructions !== undefined && !Array.isArray(value.contributedInstructions)) return false
  return true
}

function isQueuedPrompt(value: unknown): value is TaskStartupQueuedPrompt {
  return (
    isRecord(value) &&
    typeof value.id === 'string' &&
    isPreparedPrompt(value.prompt) &&
    (value.status === 'admitted' ||
      value.status === 'dispatching' ||
      value.status === 'host-queued' ||
      value.status === 'rejected') &&
    (value.failure === undefined || typeof value.failure === 'string')
  )
}

function isIdentity(value: unknown, taskId: string): value is EstablishedAhpSessionIdentity {
  return (
    isRecord(value) &&
    value.taskId === taskId &&
    typeof value.environmentId === 'string' &&
    typeof value.sessionId === 'string' &&
    (value.channel === undefined || typeof value.channel === 'string')
  )
}

export function taskStartupIdentityMatches(
  expected: EstablishedAhpSessionIdentity,
  actual: EstablishedAhpSessionIdentity,
): boolean {
  return (
    expected.taskId === actual.taskId &&
    expected.environmentId === actual.environmentId &&
    expected.sessionId === actual.sessionId &&
    (expected.channel === undefined || expected.channel === actual.channel)
  )
}

function isTaskStartupInputRecord(value: unknown): value is TaskStartupInputRecord {
  return (
    isRecord(value) &&
    value.version === TASK_STARTUP_INPUT_VERSION &&
    (value.origin === undefined || value.origin === 'startup' || value.origin === 'existing-conversation') &&
    typeof value.taskId === 'string' &&
    typeof value.launchId === 'string' &&
    typeof value.owner === 'string' &&
    Number.isInteger(value.revision) &&
    typeof value.firstPromptAccepted === 'boolean' &&
    typeof value.handoffReady === 'boolean' &&
    Array.isArray(value.queuedPrompts) &&
    value.queuedPrompts.every(isQueuedPrompt) &&
    (value.identity === undefined || isIdentity(value.identity, value.taskId))
  )
}

export function taskStartupInputOrigin(record: TaskStartupInputRecord): TaskStartupInputOrigin {
  return record.origin ?? 'startup'
}

export function taskStartupInputRequiresFirstPromptAcceptance(record: TaskStartupInputRecord): boolean {
  return taskStartupInputOrigin(record) === 'startup'
}

export function existingConversationInputLaunchId(identity: EstablishedAhpSessionIdentity): string {
  return `existing-conversation:${identity.environmentId}:${identity.sessionId}:${identity.channel ?? ''}`
}

function storageKey(taskId: string): string {
  return `${STORAGE_KEY_PREFIX}${taskId}`
}

function recordId(record: Pick<TaskStartupInputRecord, 'launchId' | 'revision'>): string {
  return `${record.launchId}:${record.revision}`
}

function inputStorage(taskId: string) {
  return createHandoffStorage<TaskStartupInputRecord>({
    key: storageKey(taskId),
    payloadChars: record =>
      TASK_STARTUP_INPUT_METADATA_RESERVE_CHARS +
      record.queuedPrompts.reduce((total, queuedPrompt) => total + preparedPromptPayloadChars(queuedPrompt.prompt), 0),
    validate: isTaskStartupInputRecord,
    getId: recordId,
  })
}

function normalizeRestoredFailures(record: TaskStartupInputRecord): TaskStartupInputRecord {
  let changed = false
  const queuedPrompts = record.queuedPrompts.map(queuedPrompt => {
    if (!queuedPrompt.failure) return queuedPrompt
    const failure =
      queuedPrompt.status === 'dispatching'
        ? queuedPrompt.failure === TASK_STARTUP_DISCONNECTED_MESSAGE
          ? TASK_STARTUP_DISCONNECTED_MESSAGE
          : TASK_STARTUP_UNCONFIRMED_DELIVERY_MESSAGE
        : queuedPrompt.status === 'rejected'
          ? SUBMIT_ERROR_MESSAGE
          : undefined
    if (failure === queuedPrompt.failure) return queuedPrompt
    changed = true
    return {...queuedPrompt, failure}
  })
  return changed ? {...record, queuedPrompts} : record
}

function currentRecord(taskId: string, launchId?: string): TaskStartupInputRecord | undefined {
  let record = records.get(taskId)
  if (record === undefined) {
    record = inputStorage(taskId).read()
    if (record) {
      const normalized = normalizeRestoredFailures(record)
      if (normalized !== record) {
        inputStorage(taskId).replace(recordId(record), normalized)
        record = normalized
      }
      for (const queuedPrompt of record.queuedPrompts) {
        if (queuedPrompt.status === 'dispatching' && queuedPrompt.failure === undefined) {
          restoredDispatches.add(queuedPromptKey(record.taskId, record.launchId, queuedPrompt.id))
        }
      }
    }
  }
  records.set(taskId, record)
  if (!record || (launchId !== undefined && record.launchId !== launchId)) return undefined
  const owner = getCurrentUserLogin()
  return owner && record.owner === owner ? record : undefined
}

function refusalMessage(reason: HandoffFailure | 'superseded'): string {
  switch (reason) {
    case 'too-large':
      return 'This message is too large to keep while the environment is starting.'
    case 'storage-full':
      return 'This message could not be queued because this tab has no storage space left.'
    case 'unavailable':
      return 'This message could not be queued because temporary tab storage is unavailable.'
    case 'superseded':
      return 'This message belongs to a startup attempt that is no longer active.'
  }
}

function replaceRecord(
  record: TaskStartupInputRecord,
  update: (record: TaskStartupInputRecord) => Omit<TaskStartupInputRecord, 'revision'>,
): HandoffFailure | 'superseded' | null {
  const next = {...update(record), revision: record.revision + 1}
  const result = inputStorage(record.taskId).replace(recordId(record), next)
  if (!result) {
    records.set(record.taskId, next)
    notify()
  }
  return result
}

function replaceAuthoritativeRecord(
  record: TaskStartupInputRecord,
  update: (record: TaskStartupInputRecord) => Omit<TaskStartupInputRecord, 'revision'> | undefined,
): HandoffFailure | 'superseded' | null {
  const storage = inputStorage(record.taskId)
  const persistedValue = storage.read()
  const persisted = persistedValue ? normalizeRestoredFailures(persistedValue) : null
  const base = persisted?.launchId === record.launchId && persisted.revision > record.revision ? persisted : record
  const updated = update(base)
  if (!updated) {
    if (base !== record) {
      records.set(record.taskId, base)
      notify()
    }
    return 'superseded'
  }
  const next = {...updated, revision: base.revision + 1}
  const expectedId = persisted?.launchId === record.launchId ? recordId(persisted) : recordId(record)
  const result = storage.replace(expectedId, next)
  const supersedingValue = result === 'superseded' ? storage.read() : null
  const supersedingRecord = supersedingValue ? normalizeRestoredFailures(supersedingValue) : null
  if (supersedingRecord) {
    records.set(record.taskId, supersedingRecord)
    notify()
    return result
  }
  const initializationFailure = initializationFailures.get(record.taskId)
  const isMemoryOnlyInitialization =
    result === 'superseded' &&
    persisted === null &&
    initializationFailure?.launchId === record.launchId &&
    initializationFailure.owner === record.owner
  if (result !== 'superseded' || isMemoryOnlyInitialization) {
    records.set(record.taskId, next)
    notify()
  }
  return isMemoryOnlyInitialization ? null : result
}

function persistAuthoritativeRecord(record: TaskStartupInputRecord): HandoffFailure | 'superseded' | null {
  const storage = inputStorage(record.taskId)
  const persistedValue = storage.read()
  const persisted = persistedValue ? normalizeRestoredFailures(persistedValue) : null
  if (persisted?.launchId === record.launchId && persisted.revision > record.revision) {
    records.set(record.taskId, persisted)
    notify()
    return null
  }
  const expectedId = persisted?.launchId === record.launchId ? recordId(persisted) : recordId(record)
  return storage.replace(expectedId, record)
}

function clearAuthoritativeRecord(initialRecord: TaskStartupInputRecord): HandoffFailure | 'superseded' | null {
  let record = initialRecord
  const storage = inputStorage(record.taskId)
  const persistedValue = storage.read()
  const persisted = persistedValue ? normalizeRestoredFailures(persistedValue) : null
  if (persisted && persisted.launchId !== record.launchId) {
    records.set(record.taskId, persisted)
    notify()
    return 'superseded'
  }
  if (persisted && persisted.revision > record.revision) {
    records.set(record.taskId, persisted)
    notify()
    if (persisted.queuedPrompts.length > 0) return null
    record = persisted
  }
  const persistedId = persisted ? recordId(persisted) : storage.storedId()
  const initializationFailure = initializationFailures.get(record.taskId)
  if (
    persistedId === null &&
    record.queuedPrompts.length === 0 &&
    initializationFailure?.launchId === record.launchId &&
    initializationFailure.owner === record.owner
  ) {
    initializationFailures.delete(record.taskId)
    records.set(record.taskId, null)
    notify()
    return null
  }
  const expectedId = persistedId?.startsWith(`${record.launchId}:`) ? persistedId : recordId(record)
  const hasQueuedPrompts = record.queuedPrompts.length > 0
  const shouldReplace = hasQueuedPrompts || (persistedId !== null && persistedId !== recordId(record))
  const emptyRecord = hasQueuedPrompts
    ? {
        ...record,
        revision: record.revision + 1,
        queuedPrompts: [],
      }
    : record
  const replaceResult = shouldReplace ? storage.replace(expectedId, emptyRecord) : null
  if (hasQueuedPrompts && replaceResult !== 'superseded') {
    records.set(record.taskId, emptyRecord)
    notify()
  }
  if (replaceResult) return replaceResult
  const clearResult = storage.clear(recordId(emptyRecord)) ? null : 'unavailable'
  if (!clearResult) {
    records.set(record.taskId, null)
    notify()
  }
  return clearResult
}

export function subscribeTaskStartupInputStore(listener: () => void): () => void {
  listeners.add(listener)
  return () => listeners.delete(listener)
}

export function getTaskStartupInput(taskId: string | undefined): TaskStartupInputRecord | undefined {
  return taskId ? currentRecord(taskId) : undefined
}

export function isRestoredTaskStartupDispatch(taskId: string, launchId: string, id: string): boolean {
  return restoredDispatches.has(queuedPromptKey(taskId, launchId, id))
}

export function beginTaskStartupInput(
  taskId: string,
  launchId: string,
  identity?: EstablishedAhpSessionIdentity,
): HandoffFailure | 'superseded' | null {
  const owner = getCurrentUserLogin()
  if (!owner) return 'unavailable'
  const existing = currentRecord(taskId, launchId)
  const storage = inputStorage(taskId)
  const persisted = storage.read()
  if (persisted && persisted.launchId !== launchId) {
    records.set(taskId, persisted)
    notify()
  }
  if (persisted?.launchId !== launchId && persisted?.queuedPrompts.length) return 'superseded'
  const cached = currentRecord(taskId)
  if (!persisted && cached?.launchId !== launchId && cached?.queuedPrompts.length) {
    return 'superseded'
  }
  if (existing && (!persisted || persisted.launchId === launchId)) {
    if (!identity || existing.identity) return null
    return replaceRecord(existing, record => ({...record, identity}))
  }
  const record: TaskStartupInputRecord = {
    version: TASK_STARTUP_INPUT_VERSION,
    origin: 'startup',
    taskId,
    launchId,
    owner,
    revision: 0,
    identity,
    firstPromptAccepted: false,
    handoffReady: false,
    queuedPrompts: [],
  }
  const result = persisted ? storage.replace(recordId(persisted), record) : storage.write(record)
  if (result === 'superseded') {
    const latest = storage.read()
    if (latest) records.set(taskId, latest)
    notify()
    return result
  }
  if (!result) {
    initializationFailures.delete(taskId)
    records.delete(taskId)
    currentRecord(taskId)
  } else {
    initializationFailures.set(taskId, {launchId, owner, reason: result})
    records.set(taskId, record)
  }
  notify()
  return result
}

export function beginTaskExistingConversationInput(
  identity: EstablishedAhpSessionIdentity,
): HandoffFailure | 'superseded' | null {
  const owner = getCurrentUserLogin()
  if (!owner) return 'unavailable'
  const taskId = identity.taskId
  if (!taskId) return 'unavailable'
  const launchId = existingConversationInputLaunchId(identity)
  const existing = currentRecord(taskId, launchId)
  if (existing) {
    if (existing.identity && taskStartupIdentityMatches(existing.identity, identity)) return null
    return replaceAuthoritativeRecord(existing, record => ({...record, identity}))
  }

  const current = currentRecord(taskId)
  if (current) {
    if (current.queuedPrompts.length > 0) return 'superseded'
    if (!clearTaskStartupInput(taskId, current.launchId)) return 'unavailable'
  }

  const record: TaskStartupInputRecord = {
    version: TASK_STARTUP_INPUT_VERSION,
    origin: 'existing-conversation',
    taskId,
    launchId,
    owner,
    revision: 0,
    identity,
    firstPromptAccepted: false,
    handoffReady: false,
    queuedPrompts: [],
  }
  const result = inputStorage(taskId).write(record)
  if (!result) {
    initializationFailures.delete(taskId)
    records.delete(taskId)
    currentRecord(taskId)
  } else {
    initializationFailures.set(taskId, {launchId, owner, reason: result})
    records.set(taskId, record)
  }
  notify()
  return result
}

export function publishTaskStartupInputIdentity(
  taskId: string,
  launchId: string,
  identity: EstablishedAhpSessionIdentity,
): HandoffFailure | 'superseded' | null {
  const record = currentRecord(taskId, launchId)
  if (!record || identity.taskId !== taskId) return 'superseded'
  if (
    record.identity?.sessionId === identity.sessionId &&
    record.identity.environmentId === identity.environmentId &&
    record.identity.channel === identity.channel
  ) {
    return null
  }
  return replaceAuthoritativeRecord(record, current => ({...current, identity}))
}

export function acceptTaskStartupFirstPrompt(
  taskId: string,
  launchId: string,
  identity: EstablishedAhpSessionIdentity,
): HandoffFailure | 'superseded' | null {
  const record = currentRecord(taskId, launchId)
  if (!record || identity.taskId !== taskId) return 'superseded'
  return replaceAuthoritativeRecord(record, current => ({
    ...current,
    identity,
    firstPromptAccepted: true,
  }))
}

export function markTaskStartupInputReady(
  taskId: string,
  launchId: string,
  identity: EstablishedAhpSessionIdentity,
): HandoffFailure | 'superseded' | null {
  let record = currentRecord(taskId, launchId)
  const recordIdentity = record?.identity
  if (
    !record ||
    !recordIdentity ||
    (taskStartupInputRequiresFirstPromptAcceptance(record) && !record.firstPromptAccepted) ||
    !taskStartupIdentityMatches(recordIdentity, identity)
  ) {
    return 'superseded'
  }
  const initializationFailure = initializationFailures.get(taskId)
  if (
    record.queuedPrompts.length === 0 &&
    initializationFailure?.launchId === launchId &&
    initializationFailure.owner === record.owner
  ) {
    return clearAuthoritativeRecord(record)
  }
  if (record.handoffReady) {
    if (inputStorage(taskId).storedId() !== recordId(record)) {
      const result = persistAuthoritativeRecord(record)
      if (result) return result
      record = currentRecord(taskId, launchId)
      if (!record) return 'superseded'
    }
  } else {
    const result = replaceAuthoritativeRecord(record, current => ({...current, handoffReady: true}))
    if (result) return result
    record = currentRecord(taskId, launchId)
    if (!record) return 'superseded'
  }
  return record.queuedPrompts.length === 0 && !isExistingConversationInput(record)
    ? clearAuthoritativeRecord(record)
    : null
}

export function admitTaskStartupPrompt(
  taskId: string,
  launchId: string,
  prompt: PreparedPrompt,
): TaskStartupPromptAdmission {
  let record = currentRecord(taskId, launchId)
  if (!record) {
    return {status: 'refused', reason: 'superseded', message: refusalMessage('superseded')}
  }
  const initializationFailure = initializationFailures.get(taskId)
  if (initializationFailure?.launchId === launchId && initializationFailure.owner === record.owner) {
    return {
      status: 'refused',
      reason: initializationFailure.reason,
      message: refusalMessage(initializationFailure.reason),
    }
  }
  if (inputStorage(taskId).storedId() !== recordId(record)) {
    const result = persistAuthoritativeRecord(record)
    if (result) return {status: 'refused', reason: result, message: refusalMessage(result)}
    record = currentRecord(taskId, launchId)
    if (!record) return {status: 'refused', reason: 'superseded', message: refusalMessage('superseded')}
  }
  const id = crypto.randomUUID()
  const result = replaceRecord(record, current => ({
    ...current,
    queuedPrompts: [...current.queuedPrompts, {id, prompt, status: 'admitted'}],
  }))
  return result ? {status: 'refused', reason: result, message: refusalMessage(result)} : {status: 'accepted', id}
}

export function updateTaskStartupQueuedPrompt(
  taskId: string,
  launchId: string,
  id: string,
  update: Pick<TaskStartupQueuedPrompt, 'status'> & {failure?: string},
): HandoffFailure | 'superseded' | null {
  const record = currentRecord(taskId, launchId)
  if (!record) return 'superseded'
  const queuedPrompt = record.queuedPrompts.find(message => message.id === id)
  if (!queuedPrompt) return 'superseded'
  restoredDispatches.delete(queuedPromptKey(taskId, launchId, id))
  return replaceRecord(record, current => ({
    ...current,
    queuedPrompts: current.queuedPrompts.map(message => (message.id === id ? {...message, ...update} : message)),
  }))
}

export function reconcileTaskStartupQueuedPrompt(
  taskId: string,
  launchId: string,
  id: string,
  update: Pick<TaskStartupQueuedPrompt, 'status'> & {failure?: string},
): HandoffFailure | 'superseded' | null {
  const record = currentRecord(taskId, launchId)
  if (!record) return 'superseded'
  const queuedPrompt = record.queuedPrompts.find(message => message.id === id)
  if (!queuedPrompt) return 'superseded'
  const result = replaceAuthoritativeRecord(record, current => ({
    ...current,
    queuedPrompts: current.queuedPrompts.map(message => (message.id === id ? {...message, ...update} : message)),
  }))
  if (result !== 'superseded') restoredDispatches.delete(queuedPromptKey(taskId, launchId, id))
  return result
}

export function transitionTaskStartupQueuedPrompt(
  taskId: string,
  launchId: string,
  id: string,
  fromStatus: TaskStartupQueuedPrompt['status'],
  update: Pick<TaskStartupQueuedPrompt, 'status'> & {failure?: string},
): HandoffFailure | 'superseded' | null {
  const record = currentRecord(taskId, launchId)
  if (!record) return 'superseded'
  const result = replaceAuthoritativeRecord(record, current => {
    const queuedPrompt = current.queuedPrompts.find(message => message.id === id)
    if (queuedPrompt?.status !== fromStatus) return undefined
    return {
      ...current,
      queuedPrompts: current.queuedPrompts.map(message => (message.id === id ? {...message, ...update} : message)),
    }
  })
  if (result !== 'superseded') restoredDispatches.delete(queuedPromptKey(taskId, launchId, id))
  return result
}

export function removeTaskStartupQueuedPrompt(
  taskId: string,
  launchId: string,
  id: string,
  allowedStatuses?: ReadonlySet<TaskStartupQueuedPrompt['status']>,
): HandoffFailure | 'superseded' | null {
  let record = currentRecord(taskId, launchId)
  if (!record) return 'superseded'
  let queuedPrompt = record.queuedPrompts.find(message => message.id === id)
  if (!queuedPrompt || (allowedStatuses && !allowedStatuses.has(queuedPrompt.status))) return 'superseded'
  if (inputStorage(taskId).storedId() !== recordId(record)) {
    const result = persistAuthoritativeRecord(record)
    if (result) return result
    record = currentRecord(taskId, launchId)
    queuedPrompt = record?.queuedPrompts.find(message => message.id === id)
    if (!record || !queuedPrompt || (allowedStatuses && !allowedStatuses.has(queuedPrompt.status))) return 'superseded'
  }
  restoredDispatches.delete(queuedPromptKey(taskId, launchId, id))
  const queuedPrompts = record.queuedPrompts.filter(message => message.id !== id)
  if (record.handoffReady && queuedPrompts.length === 0 && !isExistingConversationInput(record)) {
    if (!inputStorage(taskId).clear(recordId(record))) return 'unavailable'
    records.set(taskId, null)
    notify()
    return null
  }
  return replaceRecord(record, current => ({
    ...current,
    queuedPrompts,
  }))
}

export function consumeTaskStartupQueuedPrompt(
  taskId: string,
  launchId: string,
  id: string,
): HandoffFailure | 'superseded' | null {
  const record = currentRecord(taskId, launchId)
  if (!record) return 'superseded'
  if (!record.queuedPrompts.some(message => message.id === id)) return 'superseded'
  restoredDispatches.delete(queuedPromptKey(taskId, launchId, id))
  const result = replaceAuthoritativeRecord(record, current => ({
    ...current,
    queuedPrompts: current.queuedPrompts.filter(message => message.id !== id),
  }))
  if (result) return result
  const current = currentRecord(taskId, launchId)
  return current?.handoffReady && current.queuedPrompts.length === 0 && !isExistingConversationInput(current)
    ? clearAuthoritativeRecord(current)
    : null
}

export function clearTaskStartupInput(taskId: string, launchId: string): boolean {
  const record = currentRecord(taskId, launchId)
  if (!record) return false
  const initializationFailure = initializationFailures.get(taskId)
  if (
    !initializationFailure ||
    initializationFailure.launchId !== launchId ||
    initializationFailure.owner !== record.owner
  ) {
    if (!inputStorage(taskId).clear(recordId(record))) return false
  }
  initializationFailures.delete(taskId)
  for (const key of restoredDispatches) {
    if (key.startsWith(`${taskId}:${launchId}:`)) restoredDispatches.delete(key)
  }
  records.set(taskId, null)
  notify()
  return true
}

export function __resetTaskStartupInputStore(taskId?: string): void {
  if (taskId) {
    inputStorage(taskId).clear()
    records.delete(taskId)
    initializationFailures.delete(taskId)
    for (const key of restoredDispatches) {
      if (key.startsWith(`${taskId}:`)) restoredDispatches.delete(key)
    }
  } else {
    records.clear()
    initializationFailures.clear()
    restoredDispatches.clear()
  }
  listeners.clear()
}
